import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import CustomCard from "../../components/CustomCard";
import CustomButton from "../../components/CustomButton";
import Popup from "../../components/Popup";
import { getHtmlPage } from "../../utils/apiClient";
import AppConstants from "../../utils/appConstants";
import "../css/InformationAccount.css";
import "../css/agreementPage.css";

export default function AgreementPage() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  
  const agreementKey = searchParams.get('key');
  const agreementName = searchParams.get('name') || 'Agreement';
  const userIdParam = searchParams.get("user_id");
  const scrollTarget = searchParams.get("scroll");
  
  const [htmlContent, setHtmlContent] = useState("");
  const [loading, setLoading] = useState(true);
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [errorPopupMessage, setErrorPopupMessage] = useState("");
  const contentRef = useRef(null);

  useEffect(() => {
    const fetchHtmlContent = async () => {
      if (!agreementKey) {
        setErrorPopupMessage(t("agreementPage.errors.missingKey") || "Agreement key is missing.");
        setShowErrorPopup(true);
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        // Get current language code (e.g., 'en', 'ar')
        const currentLanguage = i18n.language || 'en';
        // Map language codes if needed (e.g., 'en-US' -> 'en')
        const languageCode = currentLanguage.split('-')[0].toLowerCase();
        
        // Get email from storage (for registration flow - server needs it to identify user and replace placeholders)
        // Priority: registrationEmail (saved during registration) > userEmail (saved during login)
        const email = localStorage.getItem('registrationEmail') || localStorage.getItem('userEmail');
        
        const response = await getHtmlPage(languageCode, agreementKey, {
          email,
          userId: userIdParam,
        });
        const responseCode = response?.data?.CODE;
        
        if (responseCode === AppConstants.responseOk) {
          let content = response?.htmlContent || response?.data?.DATA || response?.data?.HTML || "";
          
          // Client-side placeholder replacement (for registration flow when server doesn't replace)
          // Get user data from storage
          const step2DataRaw = localStorage.getItem('registration_step2_data') || sessionStorage.getItem('registration_step2_data');
          const urbanInfoDataRaw = localStorage.getItem('urban_info_data') || sessionStorage.getItem('urban_info_data');
          const identityVerificationDataRaw = sessionStorage.getItem('identity_verification_data') || localStorage.getItem('identity_verification_data');
          
          let step2Data = null;
          let urbanInfoData = null;
          let identityVerificationData = null;
          
          try {
            if (step2DataRaw) step2Data = JSON.parse(step2DataRaw);
            if (urbanInfoDataRaw) urbanInfoData = JSON.parse(urbanInfoDataRaw);
            if (identityVerificationDataRaw) identityVerificationData = JSON.parse(identityVerificationDataRaw);
          } catch (e) {
          }
          
          // Replace placeholders with actual user data
          if (step2Data || urbanInfoData || identityVerificationData) {
            // Build full name from first and last name
            const fullName = step2Data?.firstName && step2Data?.lastName 
              ? `${step2Data.firstName} ${step2Data.lastName}`.trim()
              : (step2Data?.firstName || step2Data?.lastName || '');
            
            // Format date of birth
            let dateOfBirth = '';
            if (step2Data?.dob) {
              try {
                const dob = new Date(step2Data.dob);
                if (!isNaN(dob.getTime())) {
                  // Format as DD/MM/YYYY
                  dateOfBirth = dob.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
                }
              } catch (e) {
                // If parsing fails, use as-is
                dateOfBirth = step2Data.dob;
              }
            }
            
            // Get phone number (from step2 or urbanInfo)
            const telephoneNo = step2Data?.phoneNumber || urbanInfoData?.PHONE || '';
            
            // Get email
            const userEmail = email || step2Data?.email || '';
            
            // Get identification number (from identity verification step)
            const identificationNumber = identityVerificationData?.identificationNumber || urbanInfoData?.IDENTIFICATION_NUMBER || '';
            
            // Get nationality (country code, might need to convert to name)
            const nationality = urbanInfoData?.NATIONALITY || step2Data?.nationality || '';
            
            // Get permanent address (from urbanInfo)
            const permanentAddress = urbanInfoData?.ADDRESS || urbanInfoData?.PERMANENT_ADDRESS || '';
            // Replace placeholders in HTML content (case-insensitive and handle variations)
            content = content
              .replace(/\{\{FULL_NAME\}\}/gi, fullName || '{{FULL_NAME}}')
              .replace(/\{\{IDENTIFICATION_NUMBER\}\}/gi, identificationNumber || '{{IDENTIFICATION_NUMBER}}')
              .replace(/\{\{NATIONALITY\}\}/gi, nationality || '{{NATIONALITY}}')
              .replace(/\{\{DATE_OF_BIRTH\}\}/gi, dateOfBirth || '{{DATE_OF_BIRTH}}')
              .replace(/\{\{PERMANENT_ADDRESS\}\}/gi, permanentAddress || '{{PERMANENT_ADDRESS}}')
              .replace(/\{\{TELEPHONE_NO\}\}/gi, telephoneNo || '{{TELEPHONE_NO}}')
              .replace(/\{\{TELEPHONE\}\}/gi, telephoneNo || '{{TELEPHONE}}')
              .replace(/\{\{EMAIL\}\}/gi, userEmail || '{{EMAIL}}');
          }
          
          setHtmlContent(content);
        } else {
          setErrorPopupMessage(
            t("agreementPage.errors.fetchFailed") || 
            "Failed to load agreement content. Please try again."
          );
          setShowErrorPopup(true);
        }
      } catch (error) {
        setErrorPopupMessage(
          t("agreementPage.errors.fetchFailed") || 
          "An error occurred while loading the agreement. Please try again."
        );
        setShowErrorPopup(true);
      } finally {
        setLoading(false);
      }
    };

    fetchHtmlContent();
  }, [agreementKey, i18n.language, t, userIdParam]);

  useEffect(() => {
    if (loading) return;
    if (!htmlContent) return;
    if (scrollTarget !== "content") return;
    requestAnimationFrame(() => {
      const el = contentRef.current;
      if (el && typeof el.scrollIntoView === "function") {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    });
  }, [loading, htmlContent, scrollTarget]);

  const handleBack = () => {
    // Return to the previous screen (e.g., KYC step or profile wizard) instead of forcing home.
    navigate(-1);
  };

  return (
    <div className="page-container">
      <CustomCard>
        <div className="signup-content">
          <h1 className="signup-title">{agreementName}</h1>

          <div className="agreement-content">
            {loading ? (
              <div className="loading-content">
                {t("common.loading") || "Loading..."}
              </div>
            ) : htmlContent ? (
              <div 
                className="html-content"
                ref={contentRef}
                dangerouslySetInnerHTML={{ __html: htmlContent }}
              />
            ) : (
              <div className="no-content">
                {t("agreementPage.noContent") || "No content available for this agreement."}
              </div>
            )}
          </div>

          <div className="agreement-footer">
            <CustomButton 
              className="back-button" 
              onClick={handleBack}
            >
              {t("common.back") || "Back"}
            </CustomButton>
          </div>
        </div>
      </CustomCard>

      <Popup
        isOpen={showErrorPopup}
        onClose={() => setShowErrorPopup(false)}
        title={t("common.error") || "Error"}
        message={errorPopupMessage}
        buttonText={t("common.ok") || "OK"}
        icon="fa-circle-exclamation"
      />
    </div>
  );
}

